/**
 * The rows of the review page for a portfolio in quantities, and the two things the review does to
 * them: delete and restore. The review never edits a value; deleting or restoring a row moves its
 * market value to or from cash, as the quantity editor does. Cash is derived, never stored on its own:
 * it is the AUM minus every other row's market value (`instruments-editor/quantity/derive`).
 */
import type { EditorCompositionResponse, ReviewTicker, UserInstrumentDto } from "@eu.mdotm.api/sphere";
import { builtInSort } from "@mdotm/shadcn/legacy/utils";
import type { Map as ImmutableMap } from "immutable";
import { cashTickerToInstrument } from "$root/functional-areas/instruments-editor/builder";
import type { Instrument } from "$root/functional-areas/instruments-editor/const";
import { computeCashMarketValue, deriveWeight } from "$root/functional-areas/instruments-editor/quantity/derive";
import { markHeldByValue } from "$root/functional-areas/instruments-editor/quantity/heldByValue";
import { labSum } from "$root/utils/monetary-math";
import type { ReviewInstrument } from "../const";
import { reviewSortRank, toReviewInstrument } from "../reviewRows";

/**
 * What the review adds to an editor row: the review state the grid reads, `aliasUnsized` on a row
 * fixed with a new alias that the file gives no quantity or weight to size by, and `synthetic` on a
 * row held as a synthetic instrument (`./synthetic`).
 */
export type QuantityReviewFields = Omit<ReviewInstrument, "instrumentType" | "investmentUuid" | "id"> & {
	aliasUnsized?: boolean;
	synthetic?: boolean;
};

export type QuantityReviewRow = Instrument & QuantityReviewFields;

/**
 * The review composition as rows: each entry named as the weight review names it, with its quantity,
 * price and market value; the cash entry flagged `isCash` — matched by `cashTicker` inside the
 * composition, else built from `cashTicker` alone; a row without a quantity held by value; a blocked
 * row deleted. Cash is then re-derived against the AUM, since a blocked row starts deleted. `aum` is
 * the portfolio's, or, when it carries none, the composition's own total, cash included.
 */
export function toQuantityReviewRows(params: {
	response: EditorCompositionResponse;
	userInstruments: ImmutableMap<string, UserInstrumentDto>;
	aum: number | null;
}): { rows: QuantityReviewRow[]; aum: number } {
	const { response, userInstruments } = params;
	const composition = response.composition ?? [];
	const cashTicker = response.cashTicker;
	const cashInComposition = cashTicker?.ticker != null && composition.some((x) => x.ticker === cashTicker.ticker);

	const loaded = composition.map((ticker) => {
		const row = toQuantityReviewRow(ticker, userInstruments.get(ticker.ticker ?? "") ?? {});
		return cashTicker?.ticker != null && ticker.ticker === cashTicker.ticker ? { ...row, isCash: true } : row;
	});
	const withCash = cashTicker && !cashInComposition ? [...loaded, cashRowFromTicker(cashTicker)] : loaded;
	const rows = markHeldByValueRows(withCash);
	const aum = params.aum ?? labSum(rows, (row) => row.marketValue ?? 0);
	return { rows: sortReviewRows(rebalanceReviewCash(rows, aum)), aum };
}

/** Deletes or restores the rows `ids`, never the cash row, and moves their market value to or from cash. */
export function setDeleted(
	rows: QuantityReviewRow[],
	ids: ReadonlyArray<string>,
	deleted: boolean,
	aum: number,
): QuantityReviewRow[] {
	const targets = new Set(ids);
	return rebalanceReviewCash(
		rows.map((row) => (targets.has(row.id) && !row.isCash ? { ...row, deleted } : row)),
		aum,
	);
}

/**
 * Takes what the grid changed on `changed` — deleted, risk model, reviewed — onto the rows, never onto
 * cash, and re-derives cash.
 */
export function applyReviewChanges(
	rows: QuantityReviewRow[],
	changed: ReadonlyArray<Pick<QuantityReviewRow, "id" | "deleted" | "proxies" | "hasBeenReviewed">>,
	aum: number,
): QuantityReviewRow[] {
	const byId = new Map(changed.map((row) => [row.id, row]));
	return rebalanceReviewCash(
		rows.map((row) => {
			const change = byId.get(row.id);
			if (!change || row.isCash) {
				return row;
			}
			return { ...row, deleted: change.deleted, proxies: change.proxies, hasBeenReviewed: change.hasBeenReviewed };
		}),
		aum,
	);
}

/** The cash row takes the AUM remainder and its weight; every other row is passed through. */
export function rebalanceReviewCash(rows: QuantityReviewRow[], aum: number): QuantityReviewRow[] {
	const cashMarketValue = computeCashMarketValue(rows, aum);
	return rows.map((row) =>
		row.isCash ? { ...row, marketValue: cashMarketValue, weight: deriveWeight(cashMarketValue, aum) } : row,
	);
}

function toQuantityReviewRow(ticker: ReviewTicker, userInstrument: UserInstrumentDto): QuantityReviewRow {
	const { instrumentType, investmentUuid, ...review } = toReviewInstrument(ticker, userInstrument);
	const values = { quantity: ticker.quantity, price: ticker.price, marketValue: ticker.marketValue };
	if (instrumentType === "INVESTMENT" && investmentUuid != null) {
		return { ...review, ...values, instrumentType, investmentUuid };
	}
	return { ...review, ...values, instrumentType: "INSTRUMENT" };
}

function cashRowFromTicker(cashTicker: ReviewTicker): QuantityReviewRow {
	const cash = cashTickerToInstrument(cashTicker);
	return {
		...cash,
		name: cash.name ?? "Cash",
		delisted: false,
		deleted: false,
		marketValue: cashTicker.marketValue,
		isCash: true,
	};
}

function markHeldByValueRows(rows: QuantityReviewRow[]): QuantityReviewRow[] {
	const held = new Set(
		markHeldByValue(rows)
			.filter((row) => row.heldByValue)
			.map((row) => row.id),
	);
	return rows.map((row) => (held.has(row.id) ? { ...row, heldByValue: true } : row));
}

/** The weight review's order, with cash last. */
function sortReviewRows(rows: QuantityReviewRow[]): QuantityReviewRow[] {
	return [...rows].sort((a, b) => {
		if (a.isCash !== b.isCash) {
			return a.isCash ? 1 : -1;
		}
		return builtInSort(reviewSortRank(a), reviewSortRank(b), "desc");
	});
}
